import { cn } from '../../lib/cn';
import { clamp01 } from '../../lib/format';
import { MISSION_LABELS, type MissionType } from '../../lib/game';
import { Badge } from '../primitives/Badge';
import { Countdown } from '../primitives/Countdown';
import { Button } from '../primitives/Button';
import { FleetIcon } from '../../icons/ui-icons';

export interface FleetRowProps {
  mission: MissionType;
  /** Origin and destination in [ring:system:position] notation. */
  origin: string;
  target: string;
  /** Total ship count in the formation. */
  ships: number;
  departsAt: number | string | Date;
  arrivesAt: number | string | Date;
  /** Hostile fleets inbound on the player get the danger treatment. */
  incoming?: boolean;
  /** Attacker name, shown for incoming fleets the Phalanx has identified. */
  attacker?: string;
  onRecall?: () => void;
  onDetails?: () => void;
  className?: string;
}

const MISSION_ACCENT: Record<MissionType, 'danger' | 'silicate' | 'credit' | 'aether' | 'neutral'> = {
  attack: 'danger',
  transport: 'credit',
  spy: 'aether',
  harvest: 'silicate',
  colonize: 'credit',
  deploy: 'silicate',
  recall: 'neutral',
};

/**
 * A fleet in transit.
 *
 * The flight-progress line is the point of this row: players judge whether they
 * can react to an incoming attack by how far along the marker has travelled, so
 * the bar is drawn as an actual route from origin to target with a moving ship
 * marker rather than a generic progress bar.
 */
export function FleetRow({
  mission,
  origin,
  target,
  ships,
  departsAt,
  arrivesAt,
  incoming = false,
  attacker,
  onRecall,
  onDetails,
  className,
}: FleetRowProps) {
  const start = toMs(departsAt);
  const end = toMs(arrivesAt);
  const progress = clamp01((Date.now() - start) / Math.max(1, end - start));

  return (
    <div
      className={cn(
        'rounded-control border bg-surface-2 px-3 py-2.5',
        incoming ? 'border-danger/45 bg-danger/[0.05]' : 'border-edge-subtle',
        className,
      )}
    >
      {/*
        The badge, unit count and attacker name are all variable-length, so they
        wrap as a group rather than competing for one line — otherwise "von
        Kor-Vantis" collapses to "vo…" exactly when it matters most. The
        countdown is pinned right and never wraps.
      */}
      <div className="flex items-start gap-2">
        <FleetIcon
          size={16}
          className={cn('mt-0.5 shrink-0', incoming ? 'text-danger' : 'text-content-muted')}
        />

        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <Badge accent={incoming ? 'danger' : MISSION_ACCENT[mission]} size="sm" pulse={incoming}>
            {incoming ? 'Eingehend' : MISSION_LABELS[mission]}
          </Badge>

          <span data-sg-numeric className="text-xs text-content-secondary">
            {ships} Einheiten
          </span>

          {attacker ? (
            <span className="min-w-0 truncate text-xs text-danger-bright">von {attacker}</span>
          ) : null}
        </div>

        <Countdown
          target={end}
          urgentBelow={1800}
          className="shrink-0 text-xs font-semibold"
          doneLabel="Ankunft"
        />
      </div>

      {/* Route line: origin ●────▶──── target */}
      <div className="mt-2 flex items-center gap-2">
        <span data-sg-numeric className="shrink-0 text-label text-content-muted">
          {origin}
        </span>

        <div className="relative h-px flex-1 bg-edge">
          <div
            className={cn('absolute inset-y-0 left-0', incoming ? 'bg-danger' : 'bg-silicate')}
            style={{ width: `${progress * 100}%` }}
          />
          <span
            className={cn(
              'absolute top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[1px]',
              incoming ? 'bg-danger shadow-glow-danger' : 'bg-silicate shadow-glow',
            )}
            style={{ left: `${progress * 100}%` }}
          />
        </div>

        <span data-sg-numeric className="shrink-0 text-label text-content-muted">
          {target}
        </span>
      </div>

      {onRecall || onDetails ? (
        <div className="mt-2 flex justify-end gap-1.5">
          {onDetails ? (
            <Button variant="ghost" size="sm" onClick={onDetails}>
              Details
            </Button>
          ) : null}
          {onRecall ? (
            <Button variant="secondary" size="sm" onClick={onRecall}>
              Zurückrufen
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function toMs(value: number | string | Date): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Date.now() : parsed;
}
